/**
 * Popup Subscription Form Component
 * Shared form for all popups
 */

import React, { useState } from 'react';
import axios from 'axios';

export interface PopupFormProps {
  source: string; // 'exit_popup', 'scroll_popup', 'timed_popup', 'content_upgrade', 'landing_page'
  onSuccess?: () => void;
  onError?: (error: string) => void;
  buttonText?: string;
  showRoleField?: boolean;
  showCompanyField?: boolean;
}

const PopupForm: React.FC<PopupFormProps> = ({
  source,
  onSuccess,
  onError,
  buttonText = 'Get Weekly Land Opportunities',
  showRoleField = true,
  showCompanyField = true,
}) => {
  const [formData, setFormData] = useState({
    zip_code: '',
    state: '',
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    role: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/audience/subscribe`, {
        ...formData,
        source,
      });

      if (response.data.success) {
        onSuccess?.();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to subscribe. Please try again.';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* ZIP Code with tagline in horizontal layout */}
      <div className="bg-gradient-to-br from-blue-50 via-blue-100/50 to-blue-50 p-5 rounded-xl border-2 border-blue-300/50 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            <label htmlFor="zip_code" className="block text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="text-xl">📍</span>
              <span>Your ZIP Code</span>
            </label>
            <input
              type="text"
              id="zip_code"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
              required
              maxLength={5}
              pattern="[0-9]{5}"
              className="w-32 px-4 py-3 border-2 border-blue-400/60 rounded-lg focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-xl font-bold text-center tracking-widest bg-white shadow-inner"
              placeholder="30318"
            />
          </div>
          <div className="flex-1">
            <p className="text-sm text-blue-800 font-semibold bg-white/60 px-4 py-3 rounded-lg">
              ✨ Get land lots delivered weekly in your area
            </p>
          </div>
        </div>
      </div>

      {/* Name fields side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
            First Name *
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
            placeholder="John"
          />
        </div>

        <div>
          <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name *
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
            placeholder="Smith"
          />
        </div>
      </div>

      {/* Email full width */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
          placeholder="john@example.com"
        />
      </div>

      {/* Company and Role side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {showCompanyField && (
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
              Company (Optional)
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
              placeholder="Your Company LLC"
            />
          </div>
        )}

        {showRoleField && (
          <div className={!showCompanyField ? 'md:col-span-2' : ''}>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              I am a... *
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
            >
              <option value="">Select your role</option>
              <option value="builder">Builder</option>
              <option value="project_manager">Project Manager</option>
              <option value="developer">Developer</option>
              <option value="other">Other</option>
            </select>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-5 px-6 rounded-xl hover:from-purple-700 hover:to-indigo-700 transform hover:scale-[1.02] hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-xl text-lg mt-6"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Subscribing...
          </span>
        ) : (
          buttonText
        )}
      </button>

      {/* Privacy Note */}
      <p className="text-xs text-gray-500 text-center">
        We respect your privacy. Unsubscribe anytime.
      </p>
    </form>
  );
};

export default PopupForm;
